import { useMemo } from "react";
import { countryCodeToCountryName } from "@/utils/countries.util";
import { useTranslation } from "react-i18next";
import { HiOutlineEnvelope, HiOutlineGlobeEuropeAfrica, HiOutlinePhone } from "react-icons/hi2";

import { CompanyDetailQueryProps } from "@/hooks/api/company/useCompanyDetail";
import { useCompanyDetailEmails } from "@/hooks/api/company/useCompanyDetailEmails";
import { useCompanyDetailPhones } from "@/hooks/api/company/useCompanyDetailPhones";
import { useCompanyDetailWebsites } from "@/hooks/api/company/useCompanyDetailWebsites";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import CompanyDetailLinks from "@/components/molecules/company/detail/overview/basic/CompanyDetailLinks";
import CompanyDetailSubHeader from "@/components/molecules/company/detail/overview/basic/CompanyDetailSubHeader";
import CompanyDetailOverviewInsightTabs from "@/components/molecules/company/detail/overview/CompanyDetailOverviewInsightTabs";
import TruncatedText from "@/components/molecules/TruncatedText";

const CompanyDetailOverviewTab = ({ company }: CompanyDetailQueryProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = company;

  const id = useMemo(() => data?.id || "", [data?.id]);
  const nothingFound = t("company.detail.nothingFound");

  const formattedAddress = useMemo(() => {
    const address = data?.hq_office?.address;
    if (!address) return nothingFound;

    const { street, line2, city, state, zipcode, country_code } = address;
    const countryName = countryCodeToCountryName(country_code);

    return [street, line2, city, state, zipcode, countryName].filter((value) => value && value.trim()).join(", ");
  }, [data?.hq_office?.address]);

  return (
    <div className={"h-full px-7 pt-6"}>
      <div className={"grid grid-cols-2 gap-x-14 gap-y-4"}>
        <div className={"col-span-2"}>
          <CompanyDetailSubHeader isLoading={isLoading} title={t("company.detail.description")} skeletonCount={3}>
            <TruncatedText text={data?.description || ""} />
          </CompanyDetailSubHeader>
        </div>

        <div className={"flex flex-col gap-4"}>
          <CompanyDetailSubHeader isLoading={isLoading} title={t("company.detail.website")}>
            <CompanyDetailLinks
              links={data?.websites}
              linkType={"website"}
              count={data?.website_count || 0}
              infiniteQuery={(query) => useCompanyDetailWebsites({ id, query })}
              featuredIcon={<FeaturedIcon icon={HiOutlineGlobeEuropeAfrica} size={"lg"} color={"primary"} />}
              heading={t(`company.detail.more.website.heading`, { count: data?.website_count })}
              placeholder={t(`company.detail.more.website.placeholder`)}
            />
          </CompanyDetailSubHeader>

          <CompanyDetailSubHeader isLoading={isLoading} title={t("company.detail.phone")}>
            <CompanyDetailLinks
              links={data?.phone_numbers}
              linkType={"phone"}
              count={data?.phone_number_count || 0}
              infiniteQuery={(query) => useCompanyDetailPhones({ id, query })}
              featuredIcon={<FeaturedIcon icon={HiOutlinePhone} size={"lg"} color={"primary"} />}
              heading={t(`company.detail.more.phone.heading`, { count: data?.phone_number_count })}
              placeholder={t(`company.detail.more.phone.placeholder`)}
            />
          </CompanyDetailSubHeader>

          <CompanyDetailSubHeader isLoading={isLoading} title={t("company.detail.email")}>
            <CompanyDetailLinks
              links={data?.emails}
              linkType={"email"}
              count={data?.email_count || 0}
              infiniteQuery={(query) => useCompanyDetailEmails({ id, query })}
              featuredIcon={<FeaturedIcon icon={HiOutlineEnvelope} size={"lg"} color={"primary"} />}
              heading={t(`company.detail.more.email.heading`, { count: data?.email_count })}
              placeholder={t(`company.detail.more.email.placeholder`)}
            />
          </CompanyDetailSubHeader>
        </div>

        <div className={"flex flex-col gap-4"}>
          <CompanyDetailSubHeader isLoading={isLoading} title={t("company.detail.address")}>
            <div className={"text-sm text-tertiary"}>{formattedAddress}</div>
          </CompanyDetailSubHeader>

          <CompanyDetailSubHeader isLoading={isLoading} title={t("company.detail.founded")}>
            <div className={"text-sm text-tertiary"}>
              {data?.founding_date ? new Date(data.founding_date).toLocaleDateString() : nothingFound}
            </div>
          </CompanyDetailSubHeader>

          <CompanyDetailSubHeader isLoading={isLoading} title={t("company.detail.aliases")}>
            <div className={"text-sm text-tertiary"}>
              {data?.name_aliases && data?.name_aliases.length > 0 ? data?.name_aliases.join(", ") : nothingFound}
            </div>
          </CompanyDetailSubHeader>
        </div>
      </div>

      <CompanyDetailOverviewInsightTabs id={id} />
    </div>
  );
};

export default CompanyDetailOverviewTab;
